import { useGetCameraCapabilitiesQuery } from '@kinetic-ui/data-layer';
import { MenuItem, Select, Tooltip, Typography } from '@mui/material';
import useCameraDetailsOverlayStyles from 'components/CameraDetailsOverlay/styles';
import L from 'leaflet';
import { orderBy } from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCameraOverlayMutations } from '../hooks';
import { CameraIrisFocusControlsProps } from '../types';

export interface CameraCapabilitiesProps extends CameraIrisFocusControlsProps {
  capabilitiesMenuRef: React.RefObject<HTMLDivElement>;
}

const CameraCapabilities: React.FunctionComponent<CameraCapabilitiesProps> = (props) => {
  const classes = useCameraDetailsOverlayStyles();
  const { t } = useTranslation(['video', 'common']);

  const { data, refetchData, controlsButtonRef, setShowControls, capabilitiesMenuRef } = props;

  const cameraCapabilitiesResult = useGetCameraCapabilitiesQuery({
    fetchPolicy: 'cache-and-network',
    variables: { cameraId: data.id },
  });

  const cameraCapabilities = useMemo(() => {
    // check if camera supports all capabilites that user chose to have
    const allCameraSupportedCapabilities =
      cameraCapabilitiesResult?.data?.cameraCapabilities?.map(
        (cameraCapability) => cameraCapability.capability
      ) ?? [];
    const capabilities =
      data.capabilities?.filter((cameraCapability) =>
        allCameraSupportedCapabilities.includes(cameraCapability.capability)
      ) ?? [];

    return orderBy(capabilities, [(option) => option.capability.toLowerCase()]);
  }, [cameraCapabilitiesResult?.data?.cameraCapabilities, data.capabilities]);

  const { doCameraCapability } = useCameraOverlayMutations(data?.id, refetchData);

  return (
    <>
      {cameraCapabilities.map((cameraCapability, index) => (
        <div className={classes.cameraControls} key={`cameraCapabilityDiv_${data.id}_${index}`}>
          <Tooltip
            placement='top'
            PopperProps={{ container: controlsButtonRef.current }}
            title={t(`common:${cameraCapability.capability}`)}
          >
            <Typography fontSize='small' variant='body1' className={classes.controlText}>
              {t(`common:${cameraCapability.capability}`)}
            </Typography>
          </Tooltip>
          <Select
            onChange={(event) => {
              event.stopPropagation();
              doCameraCapability(data.id, cameraCapability.capability, event.target.value);
              setShowControls(false);
            }}
            onWheel={(event) => {
              L.DomEvent.stopPropagation(event as unknown as Event);
              event.stopPropagation();
            }}
            id={`capabilitySelect_${cameraCapability.capability}_${data.id}`}
            className={classes.capabilitiesSelectInput}
            classes={{
              icon: classes.capabilitiesSelectIcon,
            }}
            MenuProps={{
              ref: capabilitiesMenuRef,
              classes: { list: classes.capabilitiesList },
            }}
            label={''}
            value={data.capabilitiesState?.[cameraCapability.capability] ?? null}
          >
            {cameraCapability.options?.map((option) => (
              <MenuItem
                key={`capabilitySelectOption_${data.id}_${option}_${index}`}
                value={option ?? undefined}
              >
                {t(`common:${option}`)}
              </MenuItem>
            ))}
          </Select>
        </div>
      ))}
    </>
  );
};

export default CameraCapabilities;
